import React from 'react';
import { Truck, Package, Clock, CheckCircle2, Ban, FileText, ShoppingBag, MapPin } from 'lucide-react';
import {
  CustomerOrder,
  CustomerOrderStatus,
  orderStatusLabel,
  orderPaymentState,
  ORDER_PAYMENT_LABELS,
  OrderPaymentState,
} from '../../lib/customerOrders';
import { orderAreaName, useServiceAreas } from '../../lib/orderAreas';
import { fmtMoney } from './SidePanel';

export const STATUS_STYLES: Record<CustomerOrderStatus, string> = {
  new: 'bg-blue-100 text-blue-700',
  confirmed: 'bg-indigo-100 text-indigo-700',
  out_for_delivery: 'bg-amber-100 text-amber-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-600',
};

export const STATUS_ICONS: Record<CustomerOrderStatus, React.ReactNode> = {
  new: <Clock className="w-3.5 h-3.5" />,
  confirmed: <CheckCircle2 className="w-3.5 h-3.5" />,
  out_for_delivery: <Truck className="w-3.5 h-3.5" />,
  delivered: <Package className="w-3.5 h-3.5" />,
  cancelled: <Ban className="w-3.5 h-3.5" />,
};

/** Status icon, fulfillment-aware: pickup orders swap the truck for a bag. */
export function orderStatusIcon(
  status: CustomerOrderStatus,
  fulfillmentType?: 'delivery' | 'pickup'
): React.ReactNode {
  if (fulfillmentType === 'pickup' && status === 'out_for_delivery') {
    return <ShoppingBag className="w-3.5 h-3.5" />;
  }
  return STATUS_ICONS[status];
}

export const PAYMENT_STYLES: Record<OrderPaymentState, string> = {
  unpaid: 'bg-gray-100 text-gray-600',
  invoice_due: 'bg-orange-100 text-orange-700',
  partial: 'bg-amber-100 text-amber-700',
  paid: 'bg-green-100 text-green-700',
  void: 'bg-gray-200 text-gray-500',
};

export function PaymentBadge({ order }: { order: CustomerOrder }) {
  const state = orderPaymentState(order);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${PAYMENT_STYLES[state]}`}>
      <FileText className="w-3.5 h-3.5" /> {ORDER_PAYMENT_LABELS[state]}
    </span>
  );
}

/** Compact delivery/pickup badge shared by the Orders page and pinned widget. */
export function FulfillmentBadge({ type }: { type: 'delivery' | 'pickup' }) {
  const isDelivery = type === 'delivery';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
      isDelivery ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'
    }`}>
      {isDelivery ? <Truck className="w-3 h-3" /> : <Package className="w-3 h-3" />}
      {isDelivery ? 'Delivery' : 'Pickup'}
    </span>
  );
}

/** Service area the order was placed from (postal prefix → service area name). */
export function AreaBadge({ name }: { name: string }) {
  return (
    <span
      title={name}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-700 max-w-full"
    >
      <MapPin className="w-3 h-3 flex-shrink-0" />
      <span className="truncate">{name}</span>
    </span>
  );
}

export function orderCustomerDisplayName(order: CustomerOrder): string {
  if (order.customer && (order.customer.first_name || order.customer.last_name)) {
    return `${order.customer.first_name ?? ''} ${order.customer.last_name ?? ''}`.trim();
  }
  return order.contact_name || order.contact_email || order.contact_phone || 'Guest';
}

// Soft strip colors keyed by the money state of the order.
const STRIP_STYLES: Record<OrderPaymentState, string> = {
  unpaid: 'bg-gray-50 text-gray-500 border-gray-100',
  invoice_due: 'bg-orange-50 text-orange-700 border-orange-100',
  partial: 'bg-amber-50 text-amber-700 border-amber-100',
  paid: 'bg-green-50 text-green-700 border-green-100',
  void: 'bg-gray-50 text-gray-400 border-gray-100',
};

function invoiceStripText(order: CustomerOrder, pay: OrderPaymentState): string {
  const inv = order.invoice;
  if (!order.invoice_id || !inv) return 'Not invoiced';
  const num = inv.invoice_number != null ? `Invoice #${inv.invoice_number}` : 'Invoiced';
  if (pay === 'paid') return `${num} · Paid`;
  if (pay === 'void') return `${num} · Voided`;
  if (pay === 'partial') {
    return `${num} · ${fmtMoney(Number(inv.amount_paid_cents ?? 0))} of ${fmtMoney(Number(inv.total_cents ?? 0))} paid`;
  }
  return `${num} · Unpaid`;
}

/**
 * Standard order list row — the single source of truth used by the Orders
 * page, the pinned orders widget, and the customer details Orders tab.
 *
 * Layout: order number + delivery type + status + service area along the top,
 * customer and amount on the next line, date · source below, and an invoice
 * strip along the bottom (not invoiced / invoiced-unpaid / invoiced-paid,
 * with the invoice number).
 */
export function OrderRow({
  order,
  onClick,
  showCustomer = true,
}: {
  order: CustomerOrder;
  onClick: () => void;
  /** Hide the customer line on surfaces already scoped to one customer. */
  showCustomer?: boolean;
}) {
  const pay = orderPaymentState(order);
  const inv = order.invoice;
  const areas = useServiceAreas(order.business_id);
  const areaName = orderAreaName(order, areas);
  // Once invoiced the invoice total is the real number; before that, estimate.
  const amountCents = inv && inv.status !== 'void'
    ? Number(inv.total_cents ?? 0)
    : order.estimated_total_cents;

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all overflow-hidden"
    >
      <div className="px-4 pt-3 pb-2.5">
        {/* Chips row — order number, then fulfillment + status + area */}
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          <span className="text-[13px] font-bold text-gray-900 truncate">{order.order_number}</span>
          <FulfillmentBadge type={order.fulfillment_type} />
          <span className={`flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLES[order.status]}`}>
            {orderStatusIcon(order.status, order.fulfillment_type)} {orderStatusLabel(order.status, order.fulfillment_type)}
          </span>
          {areaName && <AreaBadge name={areaName} />}
        </div>
        {/* Customer + amount on one line */}
        <div className="flex items-center justify-between gap-3 mt-2">
          {showCustomer ? (
            <p className="text-[13px] font-medium text-gray-800 truncate min-w-0">{orderCustomerDisplayName(order)}</p>
          ) : (
            <span aria-hidden />
          )}
          <p className="text-[15px] font-bold text-gray-900 flex-shrink-0">{fmtMoney(amountCents)}</p>
        </div>
        {/* Date · source line */}
        <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-400">
          <Package className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">
            {new Date(order.placed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            <span className="capitalize"> · {order.source}</span>
            {order.requested_date && (
              <> · Requested {order.requested_date}{order.requested_window ? ` (${order.requested_window})` : ''}</>
            )}
          </span>
        </p>
      </div>
      <div className={`px-4 py-1.5 border-t text-[11px] font-semibold flex items-center gap-1.5 ${STRIP_STYLES[pay]}`}>
        <FileText className="w-3 h-3 flex-shrink-0" />
        <span className="truncate">{invoiceStripText(order, pay)}</span>
      </div>
    </button>
  );
}
